/* bsb-custom-ball-hub.js
   Multi-product ball configurator hub. Canvas preview + bundle builder.
   Supports: color swatches, style toggle, cap toggle, sport picker, size picker.
   Customization: logo upload, text, clip art (mutually exclusive).
   Data flows from #bsb-cbh-data island + data-* attrs on radio inputs. */

(function () {
  'use strict';

  const dataEl = document.getElementById('bsb-cbh-data');
  if (!dataEl) return;

  let data;
  try {
    data = JSON.parse(dataEl.textContent);
  } catch (err) {
    console.warn('[bsb-cbh] Failed to parse data island:', err);
    return;
  }

  /* Lazy-loads a script once; concurrent callers share one in-flight promise.
     opts.integrity enables SRI — browser will refuse the script if the hash mismatches. */
  const scriptPromises = {};
  function loadScript(src, opts = {}) {
    if (scriptPromises[src]) return scriptPromises[src];
    scriptPromises[src] = new Promise((resolve, reject) => {
      const el = document.createElement('script');
      el.src = src;
      if (opts.integrity) {
        el.integrity = opts.integrity;
        el.crossOrigin = 'anonymous';
      }
      el.onload = () => resolve();
      el.onerror = () => reject(new Error(`Failed to load ${src}`));
      document.head.appendChild(el);
    });
    return scriptPromises[src];
  }

  /* ── Data island ─────────────────────────────────────────────────── */

  const variants        = data.variants || [];
  const colorPos        = data.colorPos  != null ? data.colorPos  + 1 : null;
  const stylePos        = data.stylePos  != null ? data.stylePos  + 1 : null;
  const capPos          = data.capPos    != null ? data.capPos    + 1 : null;
  const sportPos        = data.sportPos  != null ? data.sportPos  + 1 : null;
  const sizePos         = data.sizePos   != null ? data.sizePos   + 1 : null;
  const displayNotes    = data.displayNotes   || {};
  const bundleMinMode   = data.bundleMinMode  || 'amount';
  const displayLabels   = data.displayLabels  || {};
  const fourBaseRings   = data.fourBaseRings  || false;
  const safeZone        = data.safeZone       || { size: 82, x: 0, y: 0 };
  const hardwareOffset  = data.hardwareOffset || { top: 0, bottom: 0 };
  const editorGuides    = data.editorGuides   || false;
  const acceptAiEps     = data.acceptAiEps !== false;
  const vectorNote      = data.vectorNote || '';
  const lowResMin       = data.lowResMin || 0;
  const lowResNote      = data.lowResNote || '';
  const PHOTO_SHAPES    = new Set(['none', 'circle', 'heart', 'paw']);
  const photoShapeLabels = { circle: 'Circle', heart: 'Heart', paw: 'Paw' };
  const defaultPhotoShape = PHOTO_SHAPES.has(data.photoShape) ? data.photoShape : 'none';
  const defaultPhotoBorder = !!data.photoBorder;
  const textPresets     = (data.textPresets || '').split('\n').map((p) => p.trim()).filter(Boolean);
  const rasterNote      = data.rasterNote || '';
  const pumpVariantId   = data.pumpVariantId  || 0;
  const clipartData     = data.clipart        || [];
  const logoPreviewUnsupportedMessage = data.logoPreviewUnsupportedMessage || 'No preview for this file type — drag to position your logo';

  const ALLOWED_FONTS = new Set([
    'Inter', 'Arial Black', 'Georgia', 'Impact', 'Dancing Script', 'Great Vibes',
  ]);
  const defaultFont = ALLOWED_FONTS.has(data.defaultFont) ? data.defaultFont : 'Inter';

  /* ── DOM refs ────────────────────────────────────────────────────── */

  const canvas            = document.getElementById('bsb-cbh-canvas');
  if (!canvas) return;
  const ctx               = canvas.getContext('2d');

  /* ── Mobile sticky preview ───────────────────────────────────────── */

  const previewEl     = document.getElementById('bsb-cbh-preview');
  const placeholderEl = document.getElementById('bsb-cbh-preview-placeholder');
  let previewOffscreen = false;

  if (previewEl) {
    previewEl.addEventListener('click', () => {
      if (!previewOffscreen || !placeholderEl) return;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      placeholderEl.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
    });
  }

  if (previewEl && placeholderEl) {
    let stickyActive = false;
    function updateStickyPreview() {
      if (!previewEl || !placeholderEl) return;
      /* When sticky is active, use placeholder's position — it holds the original space. */
      const target  = stickyActive ? placeholderEl : previewEl;
      const rect    = target.getBoundingClientRect();
      const hidden  = rect.bottom < 0;
      if (hidden === stickyActive) return;
      stickyActive = hidden;
      previewOffscreen = hidden;
      previewEl.classList.toggle('bsb-cbh__preview--sticky', hidden);
      placeholderEl.classList.toggle('bsb-cbh__preview-placeholder--visible', hidden);
    }
    window.addEventListener('scroll', updateStickyPreview, { passive: true });
    updateStickyPreview();
  }

  let pingTimer = null;
  function pingCanvas() {
    if (!previewOffscreen) return;
    canvas.classList.remove('bsb-cbh__canvas--updated');
    void canvas.offsetWidth; /* reflow to restart animation */
    canvas.classList.add('bsb-cbh__canvas--updated');
    clearTimeout(pingTimer);
    pingTimer = setTimeout(() => canvas.classList.remove('bsb-cbh__canvas--updated'), 600);
  }

  const colorsFieldset    = document.getElementById('bsb-cbh-colors');
  const stylesFieldset    = document.getElementById('bsb-cbh-styles');
  const capsFieldset      = document.getElementById('bsb-cbh-caps');
  const sportsFieldset    = document.getElementById('bsb-cbh-sports');
  const sizesFieldset     = document.getElementById('bsb-cbh-sizes');
  const colorNameEl       = document.getElementById('bsb-cbh-color-name');
  const priceEl           = document.getElementById('bsb-cbh-price');
  const totalPriceEl      = document.getElementById('bsb-cbh-total-price');
  const doubleSidedToggle = document.getElementById('bsb-cbh-double-sided');
  const displayMethodSel  = document.getElementById('bsb-cbh-display-method');
  const displayNoteEl     = document.getElementById('bsb-cbh-display-note');
  const safeZoneToggle    = document.getElementById('bsb-cbh-safe-zone');
  const fileInput         = document.getElementById('bsb-cbh-file-input');
  const uploadArea        = document.getElementById('bsb-cbh-upload-area');
  const uploadPreview     = document.getElementById('bsb-cbh-upload-preview');
  const previewThumb      = document.getElementById('bsb-cbh-preview-thumb');
  const previewIcon       = document.getElementById('bsb-cbh-preview-icon');
  const fileNameEl        = document.getElementById('bsb-cbh-file-name');
  const removeFileBtn     = document.getElementById('bsb-cbh-remove-file');
  const fileQualityEl     = document.getElementById('bsb-cbh-file-quality');
  const logoControls      = document.getElementById('bsb-cbh-logo-controls');
  const logoSizeInput     = document.getElementById('bsb-cbh-logo-size');
  const logoSizeLabel     = document.getElementById('bsb-cbh-logo-size-label');
  const logoRotationInput = document.getElementById('bsb-cbh-logo-rotation');
  const logoRotationLabel = document.getElementById('bsb-cbh-logo-rotation-label');
  const textInput              = document.getElementById('bsb-cbh-text-input');
  const textClearBtn           = document.getElementById('bsb-cbh-text-clear');
  const fontSelect             = document.getElementById('bsb-cbh-font');
  const textColorInput         = document.getElementById('bsb-cbh-text-color');
  const textSizeInput          = document.getElementById('bsb-cbh-text-size');
  const textSizeLabel          = document.getElementById('bsb-cbh-text-size-label');
  const textRotationInput      = document.getElementById('bsb-cbh-text-rotation');
  const textRotationLabel      = document.getElementById('bsb-cbh-text-rotation-label');
  const textCurveInput         = document.getElementById('bsb-cbh-text-curve');
  const textCurveLabel         = document.getElementById('bsb-cbh-text-curve-label');
  const textNameInput          = document.getElementById('bsb-cbh-text-name');
  const textPresetSelect       = document.getElementById('bsb-cbh-text-preset');
  const photoShapeSelect       = document.getElementById('bsb-cbh-photo-shape');
  const photoBorderToggle      = document.getElementById('bsb-cbh-photo-border');
  const clipartCatsEl          = document.getElementById('bsb-cbh-clipart-cats');
  const clipartGridEl          = document.getElementById('bsb-cbh-clipart-grid');
  const clipartOptionsEl       = document.getElementById('bsb-cbh-clipart-options');
  const clipartClearBtn        = document.getElementById('bsb-cbh-clipart-clear');
  const clipartSizeInput       = document.getElementById('bsb-cbh-clipart-size');
  const clipartSizeLabel       = document.getElementById('bsb-cbh-clipart-size-label');
  const clipartColorInput      = document.getElementById('bsb-cbh-clipart-color');
  const clipartRotationInput   = document.getElementById('bsb-cbh-clipart-rotation');
  const clipartRotationLabel   = document.getElementById('bsb-cbh-clipart-rotation-label');
  const qtyInput          = document.getElementById('bsb-cbh-qty-input');
  const qtyMinus          = document.getElementById('bsb-cbh-qty-minus');
  const qtyPlus           = document.getElementById('bsb-cbh-qty-plus');
  const addToBundleBtn    = document.getElementById('bsb-cbh-add-to-bundle');
  const addToBundleLabel  = addToBundleBtn ? addToBundleBtn.textContent : '';
  const atcBtn            = document.getElementById('bsb-cbh-atc');
  const bundleListEl      = document.getElementById('bsb-cbh-bundle-list');
  const bundleCountEl     = document.getElementById('bsb-cbh-bundle-count');
  const bundleStatusEl    = document.getElementById('bsb-cbh-bundle-status');
  const bundleTotalsEl    = document.getElementById('bsb-cbh-bundle-totals');
  const bundleSubtotalEl  = document.getElementById('bsb-cbh-bundle-subtotal');
  const bundleMinEl       = document.getElementById('bsb-cbh-bundle-min');
  const bundleMinValueEl  = document.getElementById('bsb-cbh-bundle-min-value');
  const bundleMinFillEl   = document.getElementById('bsb-cbh-bundle-min-fill');
  const bundleMinHelperEl = document.getElementById('bsb-cbh-bundle-min-helper');
  const pumpCheckbox      = document.getElementById('bsb-cbh-pump-checkbox');

  const tabs           = Array.from(document.querySelectorAll('.bsb-cbh__tab'));
  const tabPanels      = Array.from(document.querySelectorAll('.bsb-cbh__tab-panel'));
  const layerOrderEl   = document.getElementById('bsb-cbh-layer-order');
  const layerChipsEl   = document.getElementById('bsb-cbh-layer-chips');
  const layerHiddenNoteEl = document.getElementById('bsb-cbh-layer-hidden-note');

  /* ── State ───────────────────────────────────────────────────────── */

  const unitScale = bundleMinMode === 'amount' ? 100 : 1;
  const bundleMin = (bundleMinEl ? parseInt(bundleMinEl.dataset.min, 10) : 1500) * unitScale;

  const state = {
    color: data.currentColor || null,
    style: data.currentStyle || null,
    cap:   data.currentCap   || null,
    sport: data.currentSport || null,
    size:  data.currentSize  || null,
    display: 'ground',
    doubleSided: false,
    showSafeZone: false,
    /* logo */
    logoImg: null,
    logoFile: null,
    logoPreviewUnsupported: false,
    logoSize: 50,
    logoRotation: 0,
    logoShape: defaultPhotoShape,
    logoBorder: defaultPhotoBorder,
    logoX: 50,
    logoY: 50,
    /* text */
    text: '',
    textFont: defaultFont,
    textColor: '#ffffff',
    textSize: 32,
    textRotation: 0,
    textX: 50,
    textY: 50,
    textCurve: 0,
    /* clipart */
    clipartImg: null,
    clipartUrl: '',
    clipartSvg: '',
    clipartColor: '#ffffff',
    clipartSize: 40,
    clipartRotation: 0,
    clipartX: 50,
    clipartY: 50,
    /* qty + drag */
    qty: 1,
    dragging: null,
    /* layer order — bottom to top */
    layerOrder: ['logo', 'clipart', 'text'],
    hiddenLayers: new Set(),
  };

  const bundle = [];
  let bundleItemSeq = 0;
  let prevMinimumMet = false;
  const ballImageCache = {};
  let sampleImg = null;
  let capturing = false;

  /* ── Variant lookup ──────────────────────────────────────────────── */

  function findVariant(opts) {
    return variants.find((v) => {
      if (colorPos && opts.color != null && v['option' + colorPos] !== opts.color) return false;
      if (stylePos && opts.style != null && v['option' + stylePos] !== opts.style) return false;
      if (capPos   && opts.cap   != null && v['option' + capPos]   !== opts.cap)   return false;
      if (sportPos && opts.sport != null && v['option' + sportPos] !== opts.sport) return false;
      if (sizePos  && opts.size  != null && v['option' + sizePos]  !== opts.size)  return false;
      return true;
    }) || null;
  }

  function activeChecked(name) {
    return document.querySelector(`input[name="${name}"]:checked`);
  }

  function currentVariant() {
    return findVariant({
      color: state.color,
      style: state.style,
      cap:   state.cap,
      sport: state.sport,
      size:  state.size,
    });
  }

  /* ── Ball image loading ──────────────────────────────────────────── */

  function loadBallImage(input, onLoad) {
    if (!input) return;
    const key = input.value;
    if (ballImageCache[key]) { if (onLoad) onLoad(); return; }
    const url = input.getAttribute('data-ball-image-900') || input.getAttribute('data-ball-image-1200');
    if (!url) return;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      ballImageCache[key] = img;
      if (onLoad) onLoad();
      else render();
    };
    img.src = url;
  }

  function activeBallImageKey() {
    /* Priority: sport > color (whichever option this product has) */
    if (sportPos && state.sport) return state.sport;
    if (colorPos && state.color) return state.color;
    return null;
  }

  /* ── Price ───────────────────────────────────────────────────────── */

  function formatPrice(dollars) {
    return `$${dollars.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  function getUnitPrice() {
    const v = currentVariant();
    if (!v) return 0;
    return v.price / 100;
  }

  function updatePrice() {
    const unit  = getUnitPrice();
    const total = unit * state.qty;
    if (priceEl) priceEl.textContent = formatPrice(unit);
    if (totalPriceEl) totalPriceEl.textContent = formatPrice(total);
    if (addToBundleBtn) {
      const v = currentVariant();
      const soldOut = !v || !v.available;
      addToBundleBtn.disabled = soldOut;
      addToBundleBtn.textContent = soldOut ? 'Sold out' : addToBundleLabel;
    }
  }

  /* ── Canvas render ───────────────────────────────────────────────── */

  function wrapText(context, text, cx, cy, maxWidth, lineHeight, maxHeight) {
    const words = text.split(' ');
    const lines = [];
    let line = '';
    words.forEach((word) => {
      const test = line ? `${line} ${word}` : word;
      if (context.measureText(test).width > maxWidth && line) { lines.push(line); line = word; }
      else line = test;
    });
    if (line) lines.push(line);
    const totalH = lines.length * lineHeight;
    if (maxHeight != null && totalH > maxHeight) return false;
    const startY = cy - totalH / 2 + lineHeight / 2;
    lines.forEach((l, i) => context.fillText(l, cx, startY + i * lineHeight));
    return true;
  }

  function render() {
    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);

    /* Ball image — draw at fixed r*2.38 so image padding doesn't affect size */
    const cx = w / 2;
    const cy = h / 2;
    const r  = w * 0.38;
    const key = activeBallImageKey();
    const img = key ? ballImageCache[key] : null;
    const ballR = img ? r * 1.05 : r;
    const safeR = r * (safeZone.size / 100);
    const safeX = cx + 2 * r * (safeZone.x / 100);
    const safeY = cy + 2 * r * (safeZone.y / 100);

    /* Bottom hardware drawn BEFORE ball image so ball overlaps it */
    drawHardware(cx, cy, ballR, 'bottom');

    if (img) {
      const imgSize = r * 2.38;
      ctx.drawImage(img, cx - imgSize / 2, cy - imgSize / 2, imgSize, imgSize);
    } else {
      ctx.fillStyle = '#ddd';
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fill();
    }

    /* Safe zone — color adapts to ball brightness */
    if (state.showSafeZone) {
      const safeColor = '#dc2626';
      ctx.save();
      ctx.beginPath();
      ctx.arc(safeX, safeY, safeR, 0, Math.PI * 2);
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 8;
      ctx.stroke();
      ctx.strokeStyle = safeColor;
      ctx.setLineDash([14, 8]);
      ctx.lineWidth = 4;
      ctx.stroke();
      ctx.restore();
      drawCircleText('SAFE ZONE GUIDE · NOT PRINTED · ', safeX, safeY, safeR - w * 0.03, safeColor, w * 0.028);
    }

    /* Layers — drawn in layerOrder (index 0 = bottom, last = top) */
    state.layerOrder.forEach((layer) => {
      if (state.hiddenLayers.has(layer)) return;
      if (layer === 'logo') {
        if (state.logoImg) {
          drawPhoto(state.logoImg, w, h);
        } else if (sampleImg && !state.logoPreviewUnsupported && !capturing) {
          ctx.save();
          ctx.globalAlpha = 0.85;
          drawPhoto(sampleImg, w, h);
          ctx.restore();
          drawSampleLabel(w, h);
        } else if (state.logoPreviewUnsupported) {
          const radius = safeR * (state.logoSize / 100);
          const lx = (state.logoX / 100) * w;
          const ly = (state.logoY / 100) * h;
          ctx.save();
          ctx.translate(lx, ly);
          ctx.rotate(state.logoRotation * Math.PI / 180);
          ctx.fillStyle = 'rgba(0,0,0,0.55)';
          ctx.beginPath();
          ctx.arc(0, 0, radius, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = '#fff';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          const fontSize = Math.min(18, radius * 0.2);
          if (fontSize >= 7) {
            ctx.font = `600 ${fontSize}px Inter, Arial, sans-serif`;
            wrapText(ctx, logoPreviewUnsupportedMessage, 0, 0, radius * 1.7, fontSize * 1.25, radius * 1.6);
          }
          ctx.restore();
        }
      }

      if (layer === 'clipart' && state.clipartImg) {
        const maxSize = w * 0.5 * (state.clipartSize / 100);
        const cw = state.clipartImg.width;
        const ch = state.clipartImg.height;
        const scale = Math.min(maxSize / cw, maxSize / ch);
        const dw = cw * scale;
        const dh = ch * scale;
        const cx2 = (state.clipartX / 100) * w;
        const cy2 = (state.clipartY / 100) * h;
        ctx.save();
        ctx.translate(cx2, cy2);
        ctx.rotate(state.clipartRotation * Math.PI / 180);
        ctx.drawImage(state.clipartImg, -dw / 2, -dh / 2, dw, dh);
        ctx.restore();
      }

      if (layer === 'text' && state.text) {
        ctx.save();
        const safeFont = ALLOWED_FONTS.has(state.textFont) ? state.textFont : 'Inter';
        ctx.font = `${state.textSize}px '${safeFont}', sans-serif`;
        ctx.fillStyle = state.textColor;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        const lines = state.text.split('\n');
        const lineHeight = state.textSize * 1.2;
        const totalH = lines.length * lineHeight;
        const tx = (state.textX / 100) * w;
        const ty = (state.textY / 100) * h;
        ctx.translate(tx, ty);
        ctx.rotate(state.textRotation * Math.PI / 180);
        lines.forEach((line, i) => {
          const dy = -totalH / 2 + lineHeight / 2 + i * lineHeight;
          if (state.textCurve) drawArcLine(line, dy, (w * 0.12) / (Math.abs(state.textCurve) / 100), state.textCurve > 0);
          else ctx.fillText(line, 0, dy);
        });
        ctx.restore();
      }
    });

    /* Top hardware drawn AFTER ball image so it overlaps the ball */
    drawHardware(cx, cy, ballR, 'top');
    pingCanvas();
  }

  function shapePath(shape, s) {
    const h = s / 2;
    ctx.beginPath();
    if (shape === 'circle') {
      ctx.arc(0, 0, h, 0, Math.PI * 2);
    } else if (shape === 'heart') {
      ctx.moveTo(0, h * 0.85);
      ctx.bezierCurveTo(-h * 1.15, h * 0.05, -h * 0.85, -h * 1.05, 0, -h * 0.45);
      ctx.bezierCurveTo(h * 0.85, -h * 1.05, h * 1.15, h * 0.05, 0, h * 0.85);
      ctx.closePath();
    } else if (shape === 'paw') {
      [[0, 0.2, 0.3, 0.26], [-0.34, -0.12, 0.11, 0.14], [-0.13, -0.34, 0.11, 0.14], [0.13, -0.34, 0.11, 0.14], [0.34, -0.12, 0.11, 0.14]]
        .forEach(([x, y, rx, ry]) => {
          ctx.moveTo((x + rx) * s, y * s);
          ctx.ellipse(x * s, y * s, rx * s, ry * s, 0, 0, Math.PI * 2);
        });
    }
  }

  function drawPhoto(img, w, h) {
    const maxSize = w * 0.5 * (state.logoSize / 100);
    const lw = img.width;
    const lh = img.height;
    const shaped = state.logoShape !== 'none';
    const scale = shaped ? Math.max(maxSize / lw, maxSize / lh) : Math.min(maxSize / lw, maxSize / lh);
    const dw = lw * scale;
    const dh = lh * scale;
    ctx.save();
    ctx.translate((state.logoX / 100) * w, (state.logoY / 100) * h);
    ctx.rotate(state.logoRotation * Math.PI / 180);
    if (shaped) {
      ctx.save();
      shapePath(state.logoShape, maxSize);
      ctx.clip();
      ctx.drawImage(img, -dw / 2, -dh / 2, dw, dh);
      ctx.restore();
      if (state.logoBorder) {
        shapePath(state.logoShape, maxSize);
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = Math.max(3, maxSize * 0.035);
        ctx.stroke();
      }
    } else {
      ctx.drawImage(img, -dw / 2, -dh / 2, dw, dh);
    }
    ctx.restore();
  }

  function drawSampleLabel(w, h) {
    const size = w * 0.024;
    const y = (state.logoY / 100) * h + (w * 0.25 * (state.logoSize / 100)) + size * 1.4;
    ctx.save();
    ctx.font = `700 ${size}px Arial, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.lineWidth = size * 0.3;
    ctx.lineJoin = 'round';
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.6)';
    ctx.fillStyle = '#fff';
    ctx.strokeText('SAMPLE PHOTO', (state.logoX / 100) * w, y);
    ctx.fillText('SAMPLE PHOTO', (state.logoX / 100) * w, y);
    ctx.restore();
  }

  function drawArcLine(line, dy, radius, up) {
    const chars = Array.from(line);
    const widths = chars.map((ch) => ctx.measureText(ch).width);
    let angle = -widths.reduce((a, b) => a + b, 0) / (2 * radius);
    chars.forEach((ch, i) => {
      const a = angle + widths[i] / (2 * radius);
      ctx.save();
      if (up) {
        ctx.translate(0, dy + radius);
        ctx.rotate(a);
        ctx.translate(0, -radius);
      } else {
        ctx.translate(0, dy - radius);
        ctx.rotate(-a);
        ctx.translate(0, radius);
      }
      ctx.fillText(ch, 0, 0);
      ctx.restore();
      angle += widths[i] / radius;
    });
  }

  function drawCircleText(phrase, x, y, radius, color, size) {
    ctx.save();
    ctx.font = `700 ${size}px Arial, sans-serif`;
    ctx.fillStyle = color;
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = size * 0.15;
    ctx.lineJoin = 'round';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const circumference = 2 * Math.PI * radius;
    const repeats = Math.max(1, Math.floor(circumference / ctx.measureText(phrase).width));
    const chars = phrase.repeat(repeats).split('');
    const step = (Math.PI * 2) / chars.length;
    ctx.translate(x, y);
    chars.forEach((ch, i) => {
      ctx.save();
      ctx.rotate(i * step);
      ctx.translate(0, -radius);
      ctx.strokeText(ch, 0, 0);
      ctx.fillText(ch, 0, 0);
      ctx.restore();
    });
    ctx.restore();
  }

  function drawHardware(cx, cy, r, side) {
    const d = state.display;

    function drawRing(x, y, label, above, tilt = 0) {
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(tilt);
      ctx.translate(-x, -y);
      const rs = r * 0.045;
      /* D-ring loop — arc faces away from ball */
      ctx.beginPath();
      ctx.arc(x, y, rs, above ? Math.PI : 0, above ? 0 : Math.PI);
      ctx.strokeStyle = '#888';
      ctx.lineWidth = 2;
      ctx.stroke();
      /* Mounting plate */
      ctx.beginPath();
      ctx.rect(x - rs, above ? y : y - rs * 0.8, rs * 2, rs * 0.8);
      ctx.fillStyle = '#aaa';
      ctx.fill();
      /* Screw dot */
      ctx.beginPath();
      ctx.arc(x, above ? y + rs * 0.4 : y - rs * 0.4, rs * 0.25, 0, Math.PI * 2);
      ctx.fillStyle = '#888';
      ctx.fill();
      /* Label */
      const fs = Math.max(10, r * 0.075);
      ctx.font = `${fs}px Inter, Arial, sans-serif`;
      ctx.fillStyle = '#555';
      ctx.textAlign = 'center';
      ctx.textBaseline = above ? 'bottom' : 'top';
      const labelY = above
        ? Math.max(fs * 1.3, y - rs * 2 - 4)
        : Math.min(canvas.height - fs * 1.3, y + rs * 2 + 4);
      ctx.fillText(label, x, labelY);
      ctx.restore();
    }

    const rs = r * 0.045;
    const baseAngle = Math.PI / 6;
    const baseRings = fourBaseRings && (d === 'ground' || d === 'both');
    const topY = cy - r + rs * 0.4 + 2 * r * (hardwareOffset.top / 100);
    const bottomShift = 2 * r * (hardwareOffset.bottom / 100);
    if (side === 'bottom') {
      if (d === 'ground' || d === 'both') {
        drawRing(cx, cy + r + rs * 0.6 + bottomShift, fourBaseRings ? '⚙ Valve + 4 D-rings' : '⚙ Valve + D-ring', false);
      }
      if (baseRings) {
        drawRing(cx - r * Math.sin(baseAngle), cy + r * Math.cos(baseAngle) + rs * 0.6 + bottomShift, '', false, baseAngle);
        drawRing(cx + r * Math.sin(baseAngle), cy + r * Math.cos(baseAngle) + rs * 0.6 + bottomShift, '', false, -baseAngle);
      }
    }
    if (side === 'top') {
      if (baseRings) {
        drawRing(cx, cy + r * Math.cos(baseAngle) + bottomShift, '', false);
      }
      if (d === 'hanging') {
        drawRing(cx, topY, '⚙ Valve + D-ring', true);
      }
      if (d === 'both') {
        drawRing(cx, topY, '🪝 D-ring (top)', true);
      }
    }
  }

  /* ── Option changes ──────────────────────────────────────────────── */

  function onColorChange(input) {
    state.color = input.value;
    if (colorNameEl) colorNameEl.textContent = input.dataset.colorName || input.value;
    loadBallImage(input, () => render());
    updateSizesForOptions();
    updatePrice();
  }

  function onStyleChange(input) {
    state.style = input.value;
    updateSizesForOptions();
    updatePrice();
    render();
  }

  function onCapChange(input) {
    state.cap = input.value;
    updateSizesForOptions();
    updatePrice();
    render();
  }

  function onSportChange(input) {
    state.sport = input.value;
    loadBallImage(input, () => render());
    updateSizesForOptions();
    updatePrice();
  }

  function onSizeChange() {
    const sizeInput = activeChecked('bsb-cbh-size');
    if (sizeInput) state.size = sizeInput.value;
    updatePrice();
    render();
  }

  /* Update size radio data-variant-id/data-price when a non-size option changes.
     Mirrors bsb-product-info.js updateSizePrices — no DOM rebuild, just patch attrs. */
  function updateSizesForOptions() {
    if (!sizesFieldset || !sizePos) return;
    sizesFieldset.querySelectorAll('.bsb-cbh__size-input').forEach((sizeInput) => {
      const match = findVariant({
        color: state.color,
        style: state.style,
        cap:   state.cap,
        sport: state.sport,
        size:  sizeInput.value,
      });
      if (!match) return;
      sizeInput.setAttribute('data-variant-id', match.id);
      sizeInput.setAttribute('data-price', match.price);
    });
    updatePrice();
  }

  const optionGroups = [
    { key: 'sport', pos: sportPos, name: 'bsb-cbh-sport' },
    { key: 'style', pos: stylePos, name: 'bsb-cbh-style' },
    { key: 'color', pos: colorPos, name: 'bsb-cbh-color' },
    { key: 'cap',   pos: capPos,   name: 'bsb-cbh-cap' },
    { key: 'size',  pos: sizePos,  name: 'bsb-cbh-size' },
  ].filter((g) => g.pos);

  function hasAvailableVariant(match) {
    return variants.some((v) => v.available && optionGroups.every((g) => match[g.key] == null || v['option' + g.pos] === match[g.key]));
  }

  function syncAvailability() {
    const chosen = {};
    optionGroups.forEach((g) => {
      const inputs = Array.from(document.querySelectorAll(`input[name="${g.name}"]`));
      inputs.forEach((input) => {
        input.disabled = !hasAvailableVariant({ ...chosen, [g.key]: input.value });
      });
      const checked = inputs.find((input) => input.checked);
      const fallback = inputs.find((input) => !input.disabled);
      if ((!checked || checked.disabled) && fallback) {
        fallback.checked = true;
        state[g.key] = fallback.value;
        if (g.key === 'color' && colorNameEl) colorNameEl.textContent = fallback.dataset.colorName || fallback.value;
        if (g.key === 'color' || g.key === 'sport') loadBallImage(fallback, () => render());
      }
      chosen[g.key] = state[g.key];
    });
  }

  document.addEventListener('change', (e) => {
    if (!optionGroups.some((g) => g.name === e.target.name)) return;
    if (e.target.name === 'bsb-cbh-color')  onColorChange(e.target);
    if (e.target.name === 'bsb-cbh-style')  onStyleChange(e.target);
    if (e.target.name === 'bsb-cbh-cap')    onCapChange(e.target);
    if (e.target.name === 'bsb-cbh-sport')  onSportChange(e.target);
    if (e.target.name === 'bsb-cbh-size')   onSizeChange();
    syncAvailability();
    updateSizesForOptions();
    updatePrice();
    render();
  });

  /* ── Tabs ────────────────────────────────────────────────────────── */

  function activateTab(tab, { focus } = {}) {
    tabs.forEach((t) => {
      const active = t === tab;
      t.classList.toggle('bsb-cbh__tab--active', active);
      t.setAttribute('aria-selected', active ? 'true' : 'false');
      t.setAttribute('tabindex', active ? '0' : '-1');
    });
    tabPanels.forEach((p) => { p.hidden = true; });
    const panel = document.getElementById(`bsb-cbh-tab-${tab.dataset.tab}`);
    if (panel) panel.hidden = false;
    if (focus) tab.focus();
  }

  document.querySelectorAll('.bsb-cbh__type-tab:not([aria-current])').forEach((link) => {
    link.addEventListener('click', (e) => {
      if (bundle.length === 0) return;
      e.preventDefault();
      if (confirm('Switching ball type will clear your current bundle. Continue?')) {
        window.location.href = link.href;
      }
    });
  });

  tabs.forEach((tab, i) => {
    tab.addEventListener('click', () => activateTab(tab));
    tab.addEventListener('keydown', (e) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      e.preventDefault();
      const next = e.key === 'ArrowRight'
        ? tabs[(i + 1) % tabs.length]
        : tabs[(i - 1 + tabs.length) % tabs.length];
      activateTab(next, { focus: true });
    });
  });


  /* ── Layer order ─────────────────────────────────────────────────── */

  const layerLabels = { logo: 'Image / Logo', text: 'Text', clipart: 'Clip Art' };

  function layerActive(layer) {
    if (layer === 'logo')    return !!(state.logoImg || state.logoPreviewUnsupported);
    if (layer === 'text')    return !!state.text;
    if (layer === 'clipart') return !!state.clipartImg;
    return false;
  }

  function layerShown(layer) {
    return layerActive(layer) && !state.hiddenLayers.has(layer);
  }

  const eyeIcon = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z"/><circle cx="12" cy="12" r="3"/></svg>';
  const eyeOffIcon = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M10.6 5.1A10.4 10.4 0 0 1 12 5c6.5 0 10 7 10 7a17 17 0 0 1-2.4 3.4M6.6 6.6A17 17 0 0 0 2 12s3.5 7 10 7a9.7 9.7 0 0 0 5.4-1.6"/><path d="M9.9 9.9a3 3 0 0 0 4.2 4.2"/><path d="M2 2l20 20"/></svg>';
  const removeIcon = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18"/></svg>';

  function renderLayerOrder() {
    if (!layerOrderEl || !layerChipsEl) return;
    const activeLayers = state.layerOrder.filter(layerActive);
    if (activeLayers.length < 1) { layerOrderEl.hidden = true; return; }
    layerOrderEl.hidden = false;
    if (layerHiddenNoteEl) layerHiddenNoteEl.hidden = !activeLayers.some((layer) => state.hiddenLayers.has(layer));
    layerChipsEl.innerHTML = activeLayers
      .map((layer, i, arr) => {
        const isTop    = i === arr.length - 1;
        const isBottom = i === 0;
        const isHidden = state.hiddenLayers.has(layer);
        const label    = layerLabels[layer];
        return (
          `<div class="bsb-cbh__layer-chip${isHidden ? ' bsb-cbh__layer-chip--hidden' : ''}" draggable="true" tabindex="0" data-layer="${layer}" aria-label="${label}: ${isTop ? 'Top' : isBottom ? 'Bottom' : 'Middle'} layer${isHidden ? ', hidden, not printed' : ''}. Use arrow keys to reorder.">` +
            `<span class="bsb-cbh__layer-chip-handle" aria-hidden="true">⠿</span>` +
            `<span class="bsb-cbh__layer-chip-label">${label}</span>` +
            `<span class="bsb-cbh__layer-chip-pos" aria-hidden="true">${isHidden ? 'Hidden' : isTop ? 'Top' : isBottom ? 'Bottom' : 'Middle'}</span>` +
            `<button class="bsb-cbh__layer-chip-btn" type="button" data-layer-action="toggle" aria-pressed="${isHidden}" aria-label="Hide ${label}">${isHidden ? eyeOffIcon : eyeIcon}</button>` +
            `<button class="bsb-cbh__layer-chip-btn" type="button" data-layer-action="remove" aria-label="Remove ${label}">${removeIcon}</button>` +
          `</div>`
        );
      })
      .join('');
  }

  let dragSrcLayer = null;

  if (layerChipsEl) {
    layerChipsEl.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-layer-action]');
      if (!btn) return;
      const layer = btn.closest('.bsb-cbh__layer-chip').dataset.layer;
      if (btn.dataset.layerAction === 'remove') {
        if (layer === 'logo')    clearLogo();
        if (layer === 'text')    { clearText(); if (textClearBtn) textClearBtn.hidden = true; }
        if (layer === 'clipart') clearClipart();
        const next = layerChipsEl.querySelector('[data-layer-action]');
        if (next) next.focus();
        return;
      }
      if (state.hiddenLayers.has(layer)) state.hiddenLayers.delete(layer);
      else state.hiddenLayers.add(layer);
      renderLayerOrder();
      render();
      const sameBtn = layerChipsEl.querySelector(`[data-layer="${layer}"] [data-layer-action="toggle"]`);
      if (sameBtn) sameBtn.focus();
    });

    layerChipsEl.addEventListener('dragstart', (e) => {
      const chip = e.target.closest('.bsb-cbh__layer-chip');
      if (!chip) return;
      dragSrcLayer = chip.dataset.layer;
      chip.classList.add('bsb-cbh__layer-chip--dragging');
      e.dataTransfer.effectAllowed = 'move';
    });

    layerChipsEl.addEventListener('dragover', (e) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      const chip = e.target.closest('.bsb-cbh__layer-chip');
      if (!chip || chip.dataset.layer === dragSrcLayer) return;
      layerChipsEl.querySelectorAll('.bsb-cbh__layer-chip').forEach((c) => {
        c.classList.toggle('bsb-cbh__layer-chip--over', c === chip);
      });
    });

    layerChipsEl.addEventListener('dragleave', (e) => {
      if (!layerChipsEl.contains(e.relatedTarget)) {
        layerChipsEl.querySelectorAll('.bsb-cbh__layer-chip--over').forEach((c) => c.classList.remove('bsb-cbh__layer-chip--over'));
      }
    });

    layerChipsEl.addEventListener('drop', (e) => {
      e.preventDefault();
      const chip = e.target.closest('.bsb-cbh__layer-chip');
      if (!chip || !dragSrcLayer || chip.dataset.layer === dragSrcLayer) return;
      const targetLayer = chip.dataset.layer;
      /* Reorder within the full layerOrder (which includes inactive layers) */
      const srcFullIdx = state.layerOrder.indexOf(dragSrcLayer);
      const tgtFullIdx = state.layerOrder.indexOf(targetLayer);
      state.layerOrder.splice(srcFullIdx, 1);
      state.layerOrder.splice(tgtFullIdx, 0, dragSrcLayer);
      dragSrcLayer = null;
      renderLayerOrder();
      render();
    });

    layerChipsEl.addEventListener('dragend', () => {
      dragSrcLayer = null;
      layerChipsEl.querySelectorAll('.bsb-cbh__layer-chip').forEach((c) => {
        c.classList.remove('bsb-cbh__layer-chip--dragging', 'bsb-cbh__layer-chip--over');
      });
    });

    /* Keyboard reorder: Arrow Up/Down move focused chip one position */
    layerChipsEl.addEventListener('keydown', (e) => {
      if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
      const chip = e.target.closest('.bsb-cbh__layer-chip');
      if (!chip) return;
      e.preventDefault();
      const layer = chip.dataset.layer;
      const activeVisible = state.layerOrder.filter(layerActive);
      const visIdx = activeVisible.indexOf(layer);
      const newVisIdx = e.key === 'ArrowUp' ? visIdx - 1 : visIdx + 1;
      if (newVisIdx < 0 || newVisIdx >= activeVisible.length) return;
      const swapLayer = activeVisible[newVisIdx];
      const srcFull = state.layerOrder.indexOf(layer);
      const tgtFull = state.layerOrder.indexOf(swapLayer);
      state.layerOrder.splice(srcFull, 1);
      state.layerOrder.splice(tgtFull, 0, layer);
      renderLayerOrder();
      render();
      /* Restore focus to the moved chip */
      const chips = layerChipsEl.querySelectorAll('.bsb-cbh__layer-chip');
      const newVisIdxInFull = state.layerOrder.filter(layerActive).indexOf(layer);
      if (chips[newVisIdxInFull]) chips[newVisIdxInFull].focus();
    });
  }

  /* ── Clear helpers ───────────────────────────────────────────────── */

  function clearLogo() {
    state.hiddenLayers.delete('logo');
    state.logoImg = null;
    state.logoFile = null;
    state.logoPreviewUnsupported = false;
    state.logoSize = 50;
    state.logoRotation = 0;
    state.logoShape = defaultPhotoShape;
    state.logoBorder = defaultPhotoBorder;
    if (photoShapeSelect) photoShapeSelect.value = defaultPhotoShape;
    if (photoBorderToggle) photoBorderToggle.checked = defaultPhotoBorder;
    if (fileInput) fileInput.value = '';
    if (uploadArea) uploadArea.classList.remove('bsb-cbh__upload-area--has-file');
    if (uploadPreview) uploadPreview.style.display = 'none';
    if (fileQualityEl) fileQualityEl.textContent = '';
    if (logoControls) logoControls.classList.remove('bsb-cbh__logo-controls--visible');
    if (logoSizeInput) logoSizeInput.value = 50;
    if (logoSizeLabel) logoSizeLabel.textContent = '50%';
    if (logoRotationInput) logoRotationInput.value = 0;
    if (logoRotationLabel) logoRotationLabel.textContent = '0°';
    setThumb('');
    renderLayerOrder();
    render();
  }

  function clearText() {
    state.hiddenLayers.delete('text');
    state.text = '';
    state.textFont = defaultFont;
    state.textColor = '#ffffff';
    state.textSize = 32;
    state.textRotation = 0;
    if (textInput) textInput.value = '';
    if (fontSelect) fontSelect.value = defaultFont;
    if (textColorInput) textColorInput.value = '#ffffff';
    if (textSizeInput) textSizeInput.value = 32;
    if (textSizeLabel) textSizeLabel.textContent = '32px';
    if (textRotationInput) textRotationInput.value = 0;
    if (textRotationLabel) textRotationLabel.textContent = '0°';
    state.textCurve = 0;
    if (textCurveInput) textCurveInput.value = 0;
    if (textCurveLabel) textCurveLabel.textContent = curveLabel(0);
    if (textNameInput) textNameInput.value = '';
    if (textPresetSelect) textPresetSelect.value = textPresets.length ? '0' : '';
    renderLayerOrder();
    render();
  }

  function clearClipart() {
    state.hiddenLayers.delete('clipart');
    state.clipartImg = null;
    state.clipartUrl = '';
    state.clipartSvg = '';
    state.clipartColor = '#ffffff';
    state.clipartSize = 40;
    state.clipartRotation = 0;
    if (clipartColorInput) clipartColorInput.value = '#ffffff';
    if (clipartSizeInput) clipartSizeInput.value = 40;
    if (clipartSizeLabel) clipartSizeLabel.textContent = '40%';
    if (clipartRotationInput) clipartRotationInput.value = 0;
    if (clipartRotationLabel) clipartRotationLabel.textContent = '0°';
    if (clipartGridEl) {
      clipartGridEl.querySelectorAll('.bsb-cbh__clipart-item--active').forEach((el) => {
        el.classList.remove('bsb-cbh__clipart-item--active');
        el.setAttribute('aria-selected', 'false');
      });
    }
    if (clipartOptionsEl) clipartOptionsEl.hidden = true;
    if (clipartClearBtn) clipartClearBtn.hidden = true;
    renderLayerOrder();
    render();
  }

  if (textClearBtn)    textClearBtn.addEventListener('click',    () => { clearText(); textClearBtn.hidden = true; });
  if (clipartClearBtn) clipartClearBtn.addEventListener('click', () => clearClipart());

  /* ── Print options ───────────────────────────────────────────────── */

  if (doubleSidedToggle) {
    doubleSidedToggle.addEventListener('change', (e) => {
      state.doubleSided = e.target.checked;
      updatePrice();
    });
  }

  if (displayMethodSel) {
    displayMethodSel.addEventListener('change', (e) => {
      state.display = e.target.value;
      if (displayNoteEl) displayNoteEl.textContent = displayNotes[state.display] || '';
      updatePrice();
      render();
    });
  }

  if (safeZoneToggle) {
    safeZoneToggle.addEventListener('change', (e) => {
      state.showSafeZone = e.target.checked;
      render();
    });
  }

  /* ── Text controls ───────────────────────────────────────────────── */

  let webFontsPromise = null;
  function loadWebFonts() {
    if (!webFontsPromise) {
      webFontsPromise = new Promise((resolve) => {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400&family=Dancing+Script&family=Great+Vibes&display=swap';
        link.onload = resolve;
        link.onerror = resolve;
        document.head.appendChild(link);
      });
    }
    return webFontsPromise;
  }

  function loadTextFont() {
    return loadWebFonts().then(() => {
      if (!document.fonts) return;
      return document.fonts.load(`${state.textSize}px '${state.textFont}'`).catch(() => {});
    });
  }

  function setText(value) {
    state.text = value;
    if (textClearBtn) textClearBtn.hidden = !state.text;
    renderLayerOrder();
    render();
    loadTextFont().then(render);
  }

  function applyTextPreset() {
    if (!textPresetSelect || !textNameInput || textPresetSelect.value === '') return;
    const preset = textPresets[parseInt(textPresetSelect.value, 10)] || '';
    const name = textNameInput.value.trim();
    const value = preset.includes('{name}') && !name
      ? ''
      : preset.split('{name}').join(name).split('{year}').join(new Date().getFullYear()).split('|').join('\n');
    if (textInput) textInput.value = value;
    setText(value);
  }

  function curveLabel(curve) {
    if (!curve) return 'Straight';
    return `${curve > 0 ? 'Arc up' : 'Arc down'} ${Math.abs(curve)}`;
  }

  if (textInput) {
    textInput.addEventListener('input', (e) => {
      if (textPresetSelect) textPresetSelect.value = '';
      setText(e.target.value);
    });
  }
  if (textNameInput)    textNameInput.addEventListener('input', applyTextPreset);
  if (textPresetSelect) textPresetSelect.addEventListener('change', applyTextPreset);
  if (textCurveInput) {
    textCurveInput.addEventListener('input', (e) => {
      state.textCurve = parseInt(e.target.value, 10);
      if (textCurveLabel) textCurveLabel.textContent = curveLabel(state.textCurve);
      render();
    });
  }
  if (fontSelect) {
    fontSelect.addEventListener('change', (e) => {
      if (!ALLOWED_FONTS.has(e.target.value)) return;
      state.textFont = e.target.value;
      render();
      loadTextFont().then(render);
    });
  }
  if (textColorInput)    textColorInput.addEventListener('input', (e) => { state.textColor = e.target.value; render(); });
  if (textSizeInput)     textSizeInput.addEventListener('input',  (e) => {
    state.textSize = parseInt(e.target.value, 10);
    if (textSizeLabel) textSizeLabel.textContent = `${state.textSize}px`;
    render();
  });
  if (textRotationInput) textRotationInput.addEventListener('input', (e) => {
    state.textRotation = parseInt(e.target.value, 10);
    if (textRotationLabel) textRotationLabel.textContent = `${state.textRotation}°`;
    render();
  });

  /* ── File upload ─────────────────────────────────────────────────── */

  function setThumb(dataUrl) {
    if (previewThumb) previewThumb.hidden = !dataUrl;
    if (previewThumb && dataUrl) previewThumb.src = dataUrl;
    if (previewIcon) previewIcon.hidden = !!dataUrl;
  }

  const UPLOAD_EXTENSIONS = acceptAiEps
    ? ['svg', 'ai', 'eps', 'pdf', 'png', 'jpg', 'jpeg']
    : ['svg', 'pdf', 'png', 'jpg', 'jpeg'];

  function startsWith(bytes, signature) {
    return signature.every((b, i) => bytes[i] === b);
  }

  function asciiIncludes(bytes, text) {
    return String.fromCharCode(...bytes).includes(text);
  }

  async function fileMatchesType(file, ext) {
    const bytes = new Uint8Array(await file.slice(0, 1024).arrayBuffer());
    const isPdf = asciiIncludes(bytes, '%PDF-');
    const isPostScript = startsWith(bytes, [0x25, 0x21, 0x50, 0x53]) || startsWith(bytes, [0xC5, 0xD0, 0xD3, 0xC6]);
    if (ext === 'png') return startsWith(bytes, [0x89, 0x50, 0x4E, 0x47, 0x0D, 0x0A, 0x1A, 0x0A]);
    if (ext === 'jpg' || ext === 'jpeg') return startsWith(bytes, [0xFF, 0xD8, 0xFF]);
    if (ext === 'pdf') return isPdf;
    if (ext === 'eps') return isPostScript;
    if (ext === 'ai') return isPdf || isPostScript;
    if (ext === 'svg') {
      const head = await file.slice(0, 65536).text();
      return !head.includes('\0') && /<svg[\s>]/i.test(head);
    }
    return false;
  }

  function rejectFile(message) {
    fileInput.value = '';
    if (fileQualityEl) {
      fileQualityEl.className = 'bsb-cbh__file-quality bsb-cbh__file-quality--raster';
      fileQualityEl.textContent = message;
    }
  }

  async function handleFile() {
    const file = fileInput.files[0];
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    const typesText = acceptAiEps ? 'SVG, PDF, PNG, JPG, AI or EPS' : 'SVG, PDF, PNG or JPG';
    if (!UPLOAD_EXTENSIONS.includes(ext)) {
      rejectFile(`This file type can’t be printed. Please upload ${typesText}.`);
      return;
    }
    const matches = await fileMatchesType(file, ext).catch(() => false);
    if (fileInput.files[0] !== file) return;
    if (!matches) {
      rejectFile(`This file doesn’t look like a real ${ext.toUpperCase()} file. Please export it again as ${typesText}.`);
      return;
    }
    state.logoFile = file;

    if (fileQualityEl) {
      if (['svg', 'ai', 'eps', 'pdf'].includes(ext)) {
        fileQualityEl.className = 'bsb-cbh__file-quality bsb-cbh__file-quality--vector';
        fileQualityEl.textContent = vectorNote;
      } else {
        fileQualityEl.className = 'bsb-cbh__file-quality bsb-cbh__file-quality--raster';
        fileQualityEl.textContent = rasterNote;
      }
    }

    state.logoImg = null;
    state.logoPreviewUnsupported = false;
    if (previewThumb) previewThumb.hidden = true;
    if (previewIcon)  previewIcon.hidden  = false;

    if (ext === 'pdf') {
      loadScript(
        'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js',
        { integrity: 'sha512-q+4liFwdPC/bNdhUpZx6aXDx/h77yEQtn7BPSONH9HEt3rvHPa89jcJFjrxaKzDOzJT5+LnGXYeEp3mSuMcNg==' }
      ).then(() => {
        const objectUrl = URL.createObjectURL(file);
        window.pdfjsLib.getDocument({ url: objectUrl, isEvalSupported: false }).promise.then((pdf) => {
          URL.revokeObjectURL(objectUrl);
          pdf.getPage(1).then((page) => {
            const vp = page.getViewport({ scale: 2 });
            const c = document.createElement('canvas');
            c.width = vp.width;
            c.height = vp.height;
            page.render({ canvasContext: c.getContext('2d'), viewport: vp }).promise.then(() => {
              const dataUrl = c.toDataURL();
              const img = new Image();
              img.onload = () => { state.logoImg = img; renderLayerOrder(); render(); };
              img.src = dataUrl;
              setThumb(dataUrl);
            });
          });
        }).catch(() => URL.revokeObjectURL(objectUrl));
      }).catch((err) => console.error('[bsb-cbh] pdf.js load failed', err));
    } else if (['svg', 'png', 'jpg', 'jpeg'].includes(ext)) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          state.logoImg = img;
          if (ext !== 'svg' && lowResMin && lowResNote && fileQualityEl && Math.min(img.naturalWidth, img.naturalHeight) < lowResMin) {
            fileQualityEl.className = 'bsb-cbh__file-quality bsb-cbh__file-quality--raster';
            fileQualityEl.textContent = lowResNote;
          }
          renderLayerOrder();
          render();
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
      const tr = new FileReader();
      tr.onload = (e2) => setThumb(e2.target.result);
      tr.readAsDataURL(file);
    } else {
      /* AI / EPS — no in-browser decoder. Show placeholder on canvas. */
      state.logoPreviewUnsupported = true;
      setThumb('');
      renderLayerOrder();
      render();
    }

    if (uploadArea)    uploadArea.classList.add('bsb-cbh__upload-area--has-file');
    if (uploadPreview) uploadPreview.style.display = 'flex';
    if (fileNameEl)    fileNameEl.textContent = file.name;
    if (logoControls)  logoControls.classList.add('bsb-cbh__logo-controls--visible');
  }

  if (fileInput) fileInput.addEventListener('change', handleFile);

  if (uploadArea) {
    uploadArea.addEventListener('dragover',  (e) => { e.preventDefault(); uploadArea.classList.add('bsb-cbh__upload-area--dragover'); });
    uploadArea.addEventListener('dragleave', ()  => uploadArea.classList.remove('bsb-cbh__upload-area--dragover'));
    uploadArea.addEventListener('drop', (e) => {
      e.preventDefault();
      uploadArea.classList.remove('bsb-cbh__upload-area--dragover');
      if (e.dataTransfer.files[0]) { fileInput.files = e.dataTransfer.files; handleFile(); }
    });
  }

  if (removeFileBtn) {
    removeFileBtn.addEventListener('click', (e) => { e.stopPropagation(); e.preventDefault(); clearLogo(); });
  }

  if (logoSizeInput) {
    logoSizeInput.addEventListener('input', (e) => {
      state.logoSize = parseInt(e.target.value, 10);
      if (logoSizeLabel) logoSizeLabel.textContent = `${state.logoSize}%`;
      render();
    });
  }

  if (photoShapeSelect) {
    photoShapeSelect.addEventListener('change', (e) => {
      if (!PHOTO_SHAPES.has(e.target.value)) return;
      state.logoShape = e.target.value;
      render();
    });
  }

  if (photoBorderToggle) {
    photoBorderToggle.addEventListener('change', (e) => {
      state.logoBorder = e.target.checked;
      render();
    });
  }

  if (logoRotationInput) {
    logoRotationInput.addEventListener('input', (e) => {
      state.logoRotation = parseInt(e.target.value, 10);
      if (logoRotationLabel) logoRotationLabel.textContent = `${state.logoRotation}°`;
      render();
    });
  }

  /* ── Clip art ────────────────────────────────────────────────────── */

  function recolorSvg(svgStr, color) {
    let s = svgStr.replace(/<svg([^>]*)>/, (_, attrs) => {
      attrs = attrs.replace(/\s*fill="[^"]*"/gi, '');
      return `<svg${attrs} fill="${color}">`;
    });
    s = s.replace(/(<(?!svg\b)[^>]*?)fill="(?!none|transparent|url)([^"]+)"/gi, `$1fill="${color}"`);
    s = s.replace(/stroke="(?!none|transparent|url|white|#fff)([^"]+)"/gi, `stroke="${color}"`);
    if (!s.includes('xmlns=')) s = s.replace('<svg ', '<svg xmlns="http://www.w3.org/2000/svg" ');
    return s;
  }

  function applyClipartColor(color) {
    if (!state.clipartSvg) return;
    const recolored = recolorSvg(state.clipartSvg, color);
    const b64 = btoa(unescape(encodeURIComponent(recolored)));
    const img = new Image();
    img.onload = () => { state.clipartImg = img; renderLayerOrder(); render(); };
    img.src = `data:image/svg+xml;base64,${b64}`;
  }

  function escapeHtml(s) {
    if (!s) return '';
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  let activeCatIndex = 0;

  function renderClipartCats() {
    if (!clipartCatsEl) return;
    clipartCatsEl.hidden = clipartData.length < 2;
    if (clipartData.length === 0) return;
    clipartCatsEl.innerHTML = clipartData.map((cat, i) =>
      `<button type="button" class="bsb-cbh__clipart-cat${i === 0 ? ' bsb-cbh__clipart-cat--active' : ''}" data-cat-index="${i}" role="tab" aria-selected="${i === 0}" tabindex="${i === 0 ? 0 : -1}">${escapeHtml(cat.name)}</button>`
    ).join('');
  }

  function renderClipartGrid(catIndex) {
    if (!clipartGridEl) return;
    const cat = clipartData[catIndex];
    if (!cat || !cat.items || cat.items.length === 0) {
      clipartGridEl.innerHTML = '<p class="bsb-cbh__clipart-empty">No clip art in this category.</p>';
      return;
    }
    clipartGridEl.innerHTML = cat.items.map((item) =>
      `<button type="button" class="bsb-cbh__clipart-item${state.clipartUrl === item.url ? ' bsb-cbh__clipart-item--active' : ''}" data-url="${escapeHtml(item.url)}" role="option" aria-selected="${state.clipartUrl === item.url}">
        <img src="${escapeHtml(item.url)}" alt="${escapeHtml(item.alt)}" loading="lazy" width="60" height="60">
      </button>`
    ).join('');
  }

  function switchClipartCat(idx) {
    activeCatIndex = idx;
    if (clipartCatsEl) {
      clipartCatsEl.querySelectorAll('.bsb-cbh__clipart-cat').forEach((btn, i) => {
        const active = i === idx;
        btn.classList.toggle('bsb-cbh__clipart-cat--active', active);
        btn.setAttribute('aria-selected', active);
        btn.setAttribute('tabindex', active ? '0' : '-1');
      });
    }
    renderClipartGrid(idx);
  }

  if (clipartCatsEl) {
    clipartCatsEl.addEventListener('click', (e) => {
      const btn = e.target.closest('.bsb-cbh__clipart-cat');
      if (!btn) return;
      switchClipartCat(parseInt(btn.dataset.catIndex, 10));
    });
    clipartCatsEl.addEventListener('keydown', (e) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      e.preventDefault();
      const next = e.key === 'ArrowRight'
        ? (activeCatIndex + 1) % clipartData.length
        : (activeCatIndex - 1 + clipartData.length) % clipartData.length;
      switchClipartCat(next);
      clipartCatsEl.querySelectorAll('.bsb-cbh__clipart-cat')[next]?.focus();
    });
  }

  const CLIPART_ALLOWED_ORIGINS = new Set([
    window.location.origin,
    'https://cdn.shopify.com',
  ]);

  function isSafeClipartUrl(url) {
    try {
      const { origin } = new URL(url, window.location.href);
      return CLIPART_ALLOWED_ORIGINS.has(origin);
    } catch { return false; }
  }

  if (clipartGridEl) {
    clipartGridEl.addEventListener('click', (e) => {
      const btn = e.target.closest('.bsb-cbh__clipart-item');
      if (!btn) return;
      const url = btn.dataset.url;
      if (!url || !isSafeClipartUrl(url)) return;

      /* Deselect if already selected */
      if (state.clipartUrl === url) { clearClipart(); return; }

      state.clipartUrl = url;
      fetch(url)
        .then((r) => r.text())
        .then((svgStr) => {
          state.clipartSvg = svgStr;
          applyClipartColor(state.clipartColor);
          if (clipartOptionsEl) clipartOptionsEl.hidden = false;
          if (clipartClearBtn)  clipartClearBtn.hidden  = false;
          renderClipartGrid(activeCatIndex);
        })
        .catch((err) => console.error('[bsb-cbh] Failed to load clipart SVG', err));
    });
  }

  if (clipartSizeInput) {
    clipartSizeInput.addEventListener('input', (e) => {
      state.clipartSize = parseInt(e.target.value, 10);
      if (clipartSizeLabel) clipartSizeLabel.textContent = `${state.clipartSize}%`;
      render();
    });
  }

  if (clipartColorInput) {
    clipartColorInput.addEventListener('input', (e) => {
      state.clipartColor = e.target.value;
      applyClipartColor(state.clipartColor);
    });
  }

  if (clipartRotationInput) {
    clipartRotationInput.addEventListener('input', (e) => {
      state.clipartRotation = parseInt(e.target.value, 10);
      if (clipartRotationLabel) clipartRotationLabel.textContent = `${state.clipartRotation}°`;
      render();
    });
  }

  renderClipartCats();
  renderClipartGrid(0);

  /* ── Quantity ─────────────────────────────────────────────────────── */

  if (qtyMinus) qtyMinus.addEventListener('click', () => { if (state.qty > 1) { state.qty--; qtyInput.value = state.qty; updatePrice(); } });
  if (qtyPlus)  qtyPlus.addEventListener('click',  () => { state.qty++; qtyInput.value = state.qty; updatePrice(); });
  if (qtyInput) qtyInput.addEventListener('change', (e) => {
    state.qty = Math.max(1, parseInt(e.target.value, 10) || 1);
    e.target.value = state.qty;
    updatePrice();
  });

  /* ── Canvas drag ─────────────────────────────────────────────────── */

  function hasDraggable() {
    return !!(state.logoImg || state.logoPreviewUnsupported || state.clipartImg || state.text);
  }

  function updateCanvasCursor() {
    canvas.classList.toggle('bsb-cbh__canvas--grabbing',  !!state.dragging);
    canvas.classList.toggle('bsb-cbh__canvas--grabbable', !state.dragging && hasDraggable());
  }

  function startDrag(x, y) {
    const px = (x / canvas.clientWidth)  * 100;
    const py = (y / canvas.clientHeight) * 100;
    const dist = (ax, ay) => Math.hypot(px - ax, py - ay);
    const candidates = [];
    if (layerShown('logo'))    candidates.push({ layer: 'logo',    d: dist(state.logoX,   state.logoY)   });
    if (layerShown('clipart')) candidates.push({ layer: 'clipart', d: dist(state.clipartX, state.clipartY) });
    if (layerShown('text'))    candidates.push({ layer: 'text',    d: dist(state.textX,    state.textY)    });
    const closest = candidates.sort((a, b) => a.d - b.d)[0];
    if (closest && closest.d < 12) {
      state.dragging = closest.layer;
      updateCanvasCursor();
      const tabMap = { logo: 'upload', text: 'text', clipart: 'clipart' };
      const tab = tabs.find((t) => t.dataset.tab === tabMap[closest.layer]);
      if (tab) activateTab(tab);
    }
  }

  function moveDrag(x, y) {
    if (!state.dragging) return;
    const px = Math.max(5, Math.min(95, (x / canvas.clientWidth)  * 100));
    const py = Math.max(5, Math.min(95, (y / canvas.clientHeight) * 100));
    if (state.dragging === 'logo')    { state.logoX    = px; state.logoY    = py; }
    if (state.dragging === 'clipart') { state.clipartX = px; state.clipartY = py; }
    if (state.dragging === 'text')    { state.textX    = px; state.textY    = py; }
    render();
  }

  function endDrag() {
    state.dragging = null;
    updateCanvasCursor();
  }

  canvas.addEventListener('mousedown',  (e) => startDrag(e.offsetX, e.offsetY));
  canvas.addEventListener('mousemove',  (e) => {
    if (!state.dragging) {
      const px = (e.offsetX / canvas.clientWidth)  * 100;
      const py = (e.offsetY / canvas.clientHeight) * 100;
      const dist = (ax, ay) => Math.hypot(px - ax, py - ay);
      const candidates = [];
      if (layerShown('logo'))    candidates.push(dist(state.logoX,    state.logoY));
      if (layerShown('clipart')) candidates.push(dist(state.clipartX,  state.clipartY));
      if (layerShown('text'))    candidates.push(dist(state.textX,     state.textY));
      const closest = candidates.length ? Math.min(...candidates) : Infinity;
      canvas.classList.toggle('bsb-cbh__canvas--grabbable', closest < 12);
    }
    moveDrag(e.offsetX, e.offsetY);
  });
  canvas.addEventListener('mouseup',    endDrag);
  canvas.addEventListener('mouseleave', endDrag);
  canvas.addEventListener('touchstart', (e) => { e.preventDefault(); const t = e.touches[0]; const r = canvas.getBoundingClientRect(); startDrag(t.clientX - r.left, t.clientY - r.top); }, { passive: false });
  canvas.addEventListener('touchmove',  (e) => { e.preventDefault(); const t = e.touches[0]; const r = canvas.getBoundingClientRect(); moveDrag(t.clientX - r.left, t.clientY - r.top); }, { passive: false });
  canvas.addEventListener('touchend',   endDrag);

  /* ── Bundle ──────────────────────────────────────────────────────── */

  function bundleTotalQty()    { return bundle.reduce((s, i) => s + i.qty, 0); }
  function bundleSubtotal()    { return bundle.reduce((s, i) => s + i.price * i.qty, 0); }

  const cartTotals = { qty: 0, amount: 0 };

  function applyCartState(cart) {
    const items = cart.items || [];
    cartTotals.qty    = items.reduce((s, l) => s + l.quantity, 0);
    cartTotals.amount = items.reduce((s, l) => s + l.line_price, 0);
    renderBundle();
  }

  function refreshCartTotals() {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return fetch(`${root}cart.js`).then((r) => r.json()).then(applyCartState).catch(() => {});
  }

  document.addEventListener('cart:change', (e) => { if (e.detail && e.detail.cart) applyCartState(e.detail.cart); });

  function bundleProgressValue() {
    const bv = bundleMinMode === 'amount' ? bundleSubtotal()    : bundleTotalQty();
    const cv = bundleMinMode === 'amount' ? cartTotals.amount   : cartTotals.qty;
    return bv + cv;
  }

  function bundleMinimumMet() { return bundleProgressValue() >= bundleMin; }

  function formatBundleValue(n) { return bundleMinMode === 'amount' ? formatPrice(n / 100) : `${n}`; }

  async function capturePreviewPng(label1, label2) {
    if (layerShown('text')) await Promise.race([loadTextFont(), new Promise((resolve) => setTimeout(resolve, 3000))]);
    return new Promise((resolve) => {
      const showSafeZone = state.showSafeZone;
      state.showSafeZone = false;
      capturing = true;
      render();
      capturing = false;
      canvas.toBlob((blob) => {
        if (!blob) { resolve(null); return; }
        const safe1 = label1.replace(/[^a-z0-9]/gi, '-').toLowerCase();
        const safe2 = label2.replace(/[^a-z0-9]/gi, '-').toLowerCase();
        const date  = new Date().toISOString().slice(0, 10);
        resolve(new File([blob], `preview-${safe1}-${safe2}-${date}.png`, { type: 'image/png' }));
      }, 'image/png');
      state.showSafeZone = showSafeZone;
      render();
    });
  }

  function specPosition(x, y) {
    return `${Math.round(x)}% from left, ${Math.round(y)}% from top of preview`;
  }

  function buildSpecText(item) {
    const date = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    const lines = [
      `Date: ${date}`,
      item.sport  ? `Sport: ${item.sport}`    : null,
      item.color  ? `Color: ${item.color}`    : null,
      item.style  ? `Style: ${item.style}`    : null,
      item.cap    ? `Cap: ${item.cap}`         : null,
      `Size: ${item.size}`,
      `Display Method: ${displayLabels[item.display] || item.display}`,
      `Double-Sided: ${item.doubleSided ? 'Yes' : 'No'}`,
      `Text: ${item.text || '(none)'}`,
      item.text ? `Font: ${item.textFont}` : null,
      item.text ? `Text Color: ${item.textColor}` : null,
      item.text ? `Text Size: ${item.textSize}px on ${canvas.width}px preview` : null,
      item.text ? `Text Position: ${specPosition(item.textX, item.textY)}` : null,
      item.text ? `Text Rotation: ${item.textRotation}°` : null,
      item.text && item.textCurve ? `Text Curve: ${curveLabel(item.textCurve)}` : null,
      `Logo: ${item.logoName || '(none)'}`,
      item.logoName ? `Logo Size: ${item.logoSize}%` : null,
      item.logoName ? `Logo Position: ${specPosition(item.logoX, item.logoY)}` : null,
      item.logoName ? `Logo Rotation: ${item.logoRotation}°` : null,
      item.logoName && item.logoShape !== 'none' ? `Photo Shape: ${photoShapeLabels[item.logoShape]}${item.logoBorder ? ', white border' : ''}` : null,
      item.clipartUrl ? `Clip Art: ${item.clipartUrl} — ${item.clipartColor}` : null,
      item.clipartUrl ? `Clip Art Size: ${item.clipartSize}%` : null,
      item.clipartUrl ? `Clip Art Position: ${specPosition(item.clipartX, item.clipartY)}` : null,
      item.clipartUrl ? `Clip Art Rotation: ${item.clipartRotation}°` : null,
      `Quantity: ${item.qty}`,
    ].filter(Boolean);
    return lines.join('\n');
  }

  async function addCurrentToBundle() {

    const variant = currentVariant();
    if (!variant || !variant.available || !addToBundleBtn || addToBundleBtn.disabled) return;
    addToBundleBtn.disabled = true;

    bundleItemSeq++;
    const labelA = state.sport || state.color || state.style || state.cap || '';
    const labelB = state.size  || '';
    const showLogo    = layerShown('logo');
    const showText    = layerShown('text');
    const showClipart = layerShown('clipart');

    const item = {
      id: bundleItemSeq,
      variantId: variant.id,
      sport: state.sport,
      color: state.color,
      style: state.style,
      cap:   state.cap,
      size:  state.size,
      qty:   state.qty,
      price: variant.price,
      display: state.display,
      doubleSided: state.doubleSided,
      text: showText ? state.text : '',
      textFont: state.textFont,
      textColor: state.textColor,
      textX: state.textX,
      textY: state.textY,
      textRotation: state.textRotation,
      textCurve: state.textCurve,
      textSize: state.textSize,
      logoFile: showLogo ? state.logoFile : null,
      logoName: showLogo && state.logoFile ? state.logoFile.name : '',
      logoX: state.logoX,
      logoY: state.logoY,
      logoSize: state.logoSize,
      logoRotation: state.logoRotation,
      logoShape: state.logoShape,
      logoBorder: state.logoBorder,
      clipartUrl: showClipart ? state.clipartUrl : '',
      clipartColor: state.clipartColor,
      clipartSize: state.clipartSize,
      clipartX: state.clipartX,
      clipartY: state.clipartY,
      clipartRotation: state.clipartRotation,
      ballImage: (ballImageCache[activeBallImageKey()] || {}).src || '',
    };

    item.previewPngFile = await capturePreviewPng(labelA, labelB);
    item.specText = buildSpecText(item);

    bundle.push(item);
    resetCustomization();
    updatePrice();
    renderBundle();
  }

  function resetCustomization() {
    state.qty = 1;
    state.display = 'ground';
    state.doubleSided = false;
    state.showSafeZone = false;
    if (qtyInput) qtyInput.value = 1;
    if (displayMethodSel) displayMethodSel.value = 'ground';
    if (displayNoteEl) displayNoteEl.textContent = displayNotes.ground || '';
    if (doubleSidedToggle) doubleSidedToggle.checked = false;
    if (safeZoneToggle)    safeZoneToggle.checked    = false;
    clearLogo();
    clearText();
    clearClipart();
  }

  function removeFromBundle(itemId) {
    const idx = bundle.findIndex((i) => i.id === itemId);
    if (idx >= 0) { bundle.splice(idx, 1); renderBundle(); }
  }

  function changeBundleQty(itemId, delta) {
    const item = bundle.find((i) => i.id === itemId);
    if (!item) return;
    item.qty = Math.max(1, item.qty + delta);
    renderBundle();
  }

  function renderBundleList() {
    if (!bundleListEl) return;
    if (bundle.length === 0) {
      bundleListEl.innerHTML = '<li class="bsb-cbh__bundle-empty">Add your first ball to start the bundle.</li>';
      return;
    }
    bundleListEl.innerHTML = bundle.map((item) => {
      const swatchStyle = item.ballImage ? `background-image:url(${escapeHtml(item.ballImage)});` : '';
      const parts = [item.sport, item.color, item.style, item.cap, item.size].filter(Boolean);
      const meta  = [item.doubleSided ? 'Double-sided' : null, item.text ? 'Text' : null, item.logoName ? 'Logo' : null, item.clipartUrl ? 'Clip art' : null].filter(Boolean);
      return (
        '<li class="bsb-cbh__bundle-item">' +
          `<span class="bsb-cbh__bundle-item-swatch" style="${swatchStyle}" aria-hidden="true"></span>` +
          '<div class="bsb-cbh__bundle-item-info">' +
            `<div class="bsb-cbh__bundle-item-name">${escapeHtml(parts.join(' — '))}</div>` +
            (meta.length ? `<div class="bsb-cbh__bundle-item-meta">${escapeHtml(meta.join(' · '))}</div>` : '') +
          '</div>' +
          '<div class="bsb-cbh__bundle-item-qty">' +
            `<button type="button" class="bsb-cbh__bundle-item-qty-btn" data-action="dec" data-item-id="${item.id}" aria-label="Decrease quantity">−</button>` +
            `<span class="bsb-cbh__bundle-item-qty-val">${item.qty}</span>` +
            `<button type="button" class="bsb-cbh__bundle-item-qty-btn" data-action="inc" data-item-id="${item.id}" aria-label="Increase quantity">+</button>` +
          '</div>' +
          `<span class="bsb-cbh__bundle-item-price">${formatPrice(item.price * item.qty / 100)}</span>` +
          `<button type="button" class="bsb-cbh__bundle-item-remove" data-action="remove" data-item-id="${item.id}" aria-label="Remove from bundle">✕</button>` +
        '</li>'
      );
    }).join('');
  }

  function renderBundleMin() {
    if (!bundleMinEl) return;
    const n    = bundleProgressValue();
    const met  = bundleMinimumMet();
    const pct  = bundleMin > 0 ? Math.min(n / bundleMin, 1) * 100 : 100;
    if (bundleMinFillEl) {
      bundleMinFillEl.style.width = `${pct}%`;
      bundleMinFillEl.classList.toggle('bsb-cbh__bundle-min-fill--met', met);
    }
    if (bundleMinValueEl) bundleMinValueEl.textContent = `${formatBundleValue(n)} of ${formatBundleValue(bundleMin)}`;
    if (bundleMinHelperEl) {
      if (met) { bundleMinHelperEl.textContent = ''; }
      else {
        const rem = bundleMin - n;
        bundleMinHelperEl.textContent = bundleMinMode === 'amount'
          ? `Add ${formatBundleValue(rem)} more to unlock checkout`
          : `Add ${rem} more ${rem === 1 ? 'ball' : 'balls'} to unlock checkout`;
      }
    }
  }

  function renderBundleTotals() {
    const n   = bundleTotalQty();
    const sub = bundleSubtotal();
    if (bundleTotalsEl) { if (n === 0) bundleTotalsEl.setAttribute('hidden', ''); else bundleTotalsEl.removeAttribute('hidden'); }
    if (bundleSubtotalEl) bundleSubtotalEl.textContent = formatPrice(sub / 100);
  }

  function renderBundleAtc() {
    if (!atcBtn) return;
    atcBtn.disabled = bundle.length === 0 || !bundleMinimumMet();
  }

  function renderBundleCount() {
    if (!bundleCountEl) return;
    const n = bundleTotalQty();
    const label = n === 0 ? 'Empty' : `${n} ball${n === 1 ? '' : 's'}`;
    bundleCountEl.textContent = label;
    if (bundleStatusEl) bundleStatusEl.textContent = n === 0 ? 'Bundle cleared.' : `Bundle updated: ${label}.`;
  }

  function maybeBurstConfetti() {
    const met = bundleMinimumMet();
    if (met === prevMinimumMet) return;
    prevMinimumMet = met;
    if (!met || typeof window.bsbConfetti !== 'function' || !atcBtn) return;
    const rect = atcBtn.getBoundingClientRect();
    window.bsbConfetti(80, rect.left + rect.width / 2, rect.top + rect.height / 2);
  }

  function renderBundle() {
    renderBundleList();
    renderBundleMin();
    renderBundleTotals();
    renderBundleAtc();
    renderBundleCount();
    maybeBurstConfetti();
  }

  if (addToBundleBtn) addToBundleBtn.addEventListener('click', addCurrentToBundle);

  if (bundleListEl) {
    bundleListEl.addEventListener('click', (e) => {
      const btn = e.target.closest('button[data-action]');
      if (!btn) return;
      const itemId = parseInt(btn.dataset.itemId, 10);
      if (!itemId) return;
      if (btn.dataset.action === 'inc')    changeBundleQty(itemId, 1);
      if (btn.dataset.action === 'dec')    changeBundleQty(itemId, -1);
      if (btn.dataset.action === 'remove') removeFromBundle(itemId);
    });
  }

  /* ── Add bundle to cart ──────────────────────────────────────────── */

  function buildItemFormData(item) {
    const fd = new FormData();
    fd.append('id', item.variantId);
    fd.append('quantity', item.qty);
    fd.append('properties[Display]', displayLabels[item.display] || '');
    if (item.doubleSided)  fd.append('properties[Double-Sided Print]', 'Yes');
    if (item.logoFile && item.logoShape !== 'none') fd.append('properties[Photo Shape]', `${photoShapeLabels[item.logoShape]}${item.logoBorder ? ', white border' : ''}`);
    if (item.text)         fd.append('properties[Text]', `${item.text} — ${item.textFont}, ${item.textColor}`);
    if (item.logoFile)     fd.append('properties[Logo File]', item.logoFile, item.logoFile.name);
    if (item.clipartUrl)   fd.append('properties[Clip Art]', item.clipartUrl);
    if (item.previewPngFile) fd.append('properties[Preview]', item.previewPngFile, item.previewPngFile.name);
    if (item.specText)     fd.append('properties[_spec]', item.specText);
    return fd;
  }

  async function addItemToCart(item) {
    const resp = await fetch('/cart/add.js', { method: 'POST', body: buildItemFormData(item) });
    const result = await resp.json();
    if (!resp.ok) throw new Error((result && result.description) || 'Failed to add item');
    return result;
  }

  function refreshCartUI() {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(`${root}cart.js`).then((r) => r.json()).then((cart) => {
      document.querySelectorAll('.cart-count-bubble span, .icon-cart-ball__count')
        .forEach((el) => { el.textContent = cart.item_count; });
      document.dispatchEvent(new CustomEvent('cart:change', { detail: { cart } }));
    }).catch(() => {});
  }

  if (atcBtn) {
    atcBtn.addEventListener('click', async function () {
      if (bundle.length === 0) return;

      const originalText = atcBtn.textContent;
      atcBtn.disabled = true;

      const total  = bundle.length;
      const failed = [];
      let addedCount = 0;
      let lastResult = null;
      let pumpFailed = false;

      /* Add ball bundle items */
      for (let i = 0; i < bundle.length; i++) {
        atcBtn.textContent = total > 1 ? `Adding ${i + 1} of ${total}...` : 'Adding...';
        try {
          lastResult = await addItemToCart(bundle[i]);
          addedCount++;
        } catch (err) {
          console.error('[bsb-cbh] Failed to add bundle item', err);
          failed.push(bundle[i]);
        }
      }

      /* Add pump if checked */
      if (pumpCheckbox && pumpCheckbox.checked && pumpVariantId) {
        atcBtn.textContent = 'Adding pump...';
        try {
          const pumpFd = new FormData();
          pumpFd.append('id', pumpVariantId);
          pumpFd.append('quantity', 1);
          const pumpResp = await fetch('/cart/add.js', { method: 'POST', body: pumpFd });
          lastResult = await pumpResp.json();
          if (!pumpResp.ok) throw new Error((lastResult && lastResult.description) || 'Failed to add pump');
          addedCount++;
        } catch (err) {
          console.error('[bsb-cbh] Failed to add pump', err);
          pumpFailed = true;
        }
      }

      if (addedCount > 0) {
        refreshCartUI();
        refreshCartTotals();
      }

      if (failed.length === 0 && pumpFailed) {
        bundle.length = 0;
        prevMinimumMet = false;
        renderBundle();
        atcBtn.textContent = 'Balls added — pump couldn’t be added';
        if (bundleStatusEl) bundleStatusEl.textContent = 'Your balls were added to the cart, but the pump could not be added. Please add the pump separately.';
        atcBtn.classList.add('bsb-cbh__atc--error');
        setTimeout(() => {
          atcBtn.textContent = originalText;
          atcBtn.classList.remove('bsb-cbh__atc--error');
        }, 4000);
      } else if (failed.length === 0) {
        atcBtn.textContent = 'Added ✓';
        atcBtn.classList.add('bsb-cbh__atc--success');
        setTimeout(() => {
          bundle.length = 0;
          prevMinimumMet = false;
          renderBundle();
          atcBtn.textContent = originalText;
          atcBtn.classList.remove('bsb-cbh__atc--success');
        }, 1500);
      } else {
        bundle.length = 0;
        bundle.push(...failed);
        renderBundle();
        atcBtn.textContent = addedCount > 0
          ? `${addedCount} added, ${failed.length} failed — Try Again`
          : 'Error — Try Again';
        atcBtn.classList.add('bsb-cbh__atc--error');
        setTimeout(() => {
          atcBtn.textContent = originalText;
          atcBtn.classList.remove('bsb-cbh__atc--error');
          atcBtn.disabled = !bundleMinimumMet();
        }, 3000);
      }
    });
  }

  /* ── Init ────────────────────────────────────────────────────────── */

  /* Pre-load ball image for the initially-checked color/sport input */
  const initialColorInput = activeChecked('bsb-cbh-color');
  const initialSportInput = activeChecked('bsb-cbh-sport');

  if (initialColorInput) {
    state.color = initialColorInput.value;
    if (colorNameEl) colorNameEl.textContent = initialColorInput.dataset.colorName || state.color;
    loadBallImage(initialColorInput, () => render());
  }

  if (initialSportInput) {
    state.sport = initialSportInput.value;
    loadBallImage(initialSportInput, () => render());
    updateSizesForOptions();
  }

  const initialStyleInput = activeChecked('bsb-cbh-style');
  if (initialStyleInput) state.style = initialStyleInput.value;

  const initialCapInput = activeChecked('bsb-cbh-cap');
  if (initialCapInput) state.cap = initialCapInput.value;

  const initialSizeInput = activeChecked('bsb-cbh-size');
  if (initialSizeInput) state.size = initialSizeInput.value;

  if (editorGuides) {
    state.showSafeZone = true;
    state.display = 'both';
    if (safeZoneToggle)   safeZoneToggle.checked = true;
    if (displayMethodSel) displayMethodSel.value = 'both';
    if (displayNoteEl)    displayNoteEl.textContent = displayNotes.both || '';
  }

  if (data.sampleImage) {
    const sample = new Image();
    sample.crossOrigin = 'anonymous';
    sample.onload = () => { sampleImg = sample; render(); };
    sample.src = data.sampleImage;
  }

  syncAvailability();
  updateSizesForOptions();
  updatePrice();
  renderBundle();
  refreshCartTotals();
  render();
})();
